import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Spinner, Alert, Form } from "react-bootstrap";
import api from "../../services/api";

const TermosAcceptModal = ({ show, termos, onAccept, onDecline }) => {
  const [currentTermoIndex, setCurrentTermoIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [acceptedCurrent, setAcceptedCurrent] = useState(false);
  const pdfContainerRef = useRef(null);

  useEffect(() => {
    if (show) {
      setCurrentTermoIndex(0);
      setAcceptedCurrent(false);
      setError(null);
    }
  }, [show, termos]);

  const currentTermo =
    termos.length > currentTermoIndex ? termos[currentTermoIndex] : null;

  // Função chamada ao clicar em "Aceitar e Continuar"
  const handleAcceptCurrent = async () => {
    if (!currentTermo || !acceptedCurrent) return;

    setIsLoading(true);
    setError(null);

    try {
      await api.post("/termos-plataforma/registrar-consentimento/", {
        termo_id: currentTermo.id,
        // O backend pega o 'usuario_id' do token JWT
      });

      // Se for o último termo, chama o callback onAccept (da Home)
      if (currentTermoIndex >= termos.length - 1) {
        onAccept();
      } else {
        // Se houver mais termos, avança para o próximo
        setCurrentTermoIndex((prev) => prev + 1);
        setAcceptedCurrent(false); // Reseta o checkbox
      }
    } catch (err) {
      console.error("Erro ao registrar consentimento:", err);
      let errMsg = "Erro ao registrar aceite.";
      if (err.response && err.response.data) {
        try {
          const errors = err.response.data;
          const messages = Object.keys(errors).map(
            (key) =>
              `${key}: ${
                Array.isArray(errors[key])
                  ? errors[key].join(", ")
                  : errors[key]
              }`
          );
          errMsg = messages.join("\n");
        } catch (e) {
          errMsg = JSON.stringify(err.response.data);
        }
      }
      setError(errMsg);

      if (err.response && err.response.status === 401) {
        onDecline();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalHide = () => {
    setError("É necessário aceitar todos os termos para continuar.");
  };

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header>
        <Modal.Title>
          {currentTermo
            ? `Aceite Obrigatório: ${currentTermo.titulo} (v${currentTermo.versao})`
            : "Termos Pendentes"}
          {termos.length > 1 &&
            ` (${currentTermoIndex + 1} de ${termos.length})`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {/* Visualizador de PDF */}
        {currentTermo?.arquivo_pdf_url ? (
          <div
            ref={pdfContainerRef}
            style={{
              height: "60vh",
              overflowY: "auto",
              border: "1px solid #ccc",
            }}
          >
            <embed
              src={currentTermo.arquivo_pdf_url}
              type="application/pdf"
              width="100%"
              height="100%"
            />
            <p className="p-3 text-center">
              Se o PDF não carregar, por favor{" "}
              <a
                href={currentTermo.arquivo_pdf_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                clique aqui para abri-lo
              </a>
              .
            </p>
          </div>
        ) : (
          <div className="text-center p-5">
            <Spinner animation="border" />
          </div>
        )}

        {/* Checkbox de Aceite */}
        <Form.Check
          type="checkbox"
          id={`accept-termo-${currentTermo?.id}`}
          label="Li e aceito os termos apresentados acima."
          checked={acceptedCurrent}
          onChange={(e) => setAcceptedCurrent(e.target.checked)}
          className="mt-3"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={onDecline} disabled={isLoading}>
          Recusar e Sair
        </Button>
        <Button
          variant="success"
          onClick={handleAcceptCurrent}
          disabled={isLoading || !acceptedCurrent || !currentTermo}
        >
          {isLoading ? (
            <Spinner as="span" animation="border" size="sm" />
          ) : (
            "Aceitar e Continuar"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

TermosAcceptModal.propTypes = {
  show: PropTypes.bool.isRequired,
  termos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      titulo: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      versao: PropTypes.number.isRequired,
      arquivo_pdf_url: PropTypes.string,
    })
  ).isRequired,
  userId: PropTypes.number,
  onAccept: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
};

export default TermosAcceptModal;